import { useState } from "react"

import API from "web/hooks/data/api"
import useModals from "./useModals"
import useAutoFocusInput from "web/hooks/behaviour/useAutoFocusInput"

import { Button } from "web/components"
import CompanyLookup from "web/components/Form/Controls/CompanyLookup"
import ControlContainer from "web/components/Form/Controls/ControlContainer"

const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function prefilledAttributes(name) {
  if (EMAIL_ADDRESS_PATTERN.test(name)) {
    return { email_address: name }
  }

  const [first_name, ...rest] = name.trim().split(" ")

  return { first_name, last_name: rest.join(" ") }
}

function errorsByAttribute(errors) {
  return errors.reduce(
    (messages, { attribute, message }) => ({
      ...messages,
      [attribute]: [messages[attribute], message].filter(Boolean).join(", "),
    }),
    {}
  )
}

function TextField({
  name,
  label,
  type = "text",
  value,
  onChange,
  errors,
  inputRef,
}) {
  return (
    <ControlContainer
      name={name}
      label={label}
      error={errors[name]}
      errorCondition={name in errors}
    >
      <input
        ref={inputRef}
        type={type}
        name={name}
        className="form-control"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </ControlContainer>
  )
}

export function CreateContactModal({ name, respond }) {
  const [attributes, setAttributes] = useState({
    first_name: "",
    last_name: "",
    company_position: "",
    email_address: "",
    primary_telephone_number: "",
    ...prefilledAttributes(name),
  })
  const [company, setCompany] = useState(null)
  const [errors, setErrors] = useState({})
  const { inputRef } = useAutoFocusInput()

  function setAttribute(attribute) {
    return (value) => setAttributes({ ...attributes, [attribute]: value })
  }

  async function handleCreate() {
    try {
      const contact = await API.post("/contacts", {
        ...attributes,
        company_id: company ? company.value : null,
      })
      respond(contact)
    } catch (error) {
      setErrors(errorsByAttribute(error.response.data.errors))
    }
  }

  return (
    <>
      <div className="modal-content-above-commands">
        <div className="grid gap-y-3">
          <div className="grid grid-cols-2 gap-x-3">
            <TextField
              name="first_name"
              label="First Name"
              value={attributes.first_name}
              onChange={setAttribute("first_name")}
              errors={errors}
              inputRef={inputRef}
            />
            <TextField
              name="last_name"
              label="Last Name"
              value={attributes.last_name}
              onChange={setAttribute("last_name")}
              errors={errors}
            />
          </div>
          <TextField
            name="company_position"
            label="Title"
            value={attributes.company_position}
            onChange={setAttribute("company_position")}
            errors={errors}
          />
          <CompanyLookup
            name="company"
            label="Company"
            value={company}
            onChange={setCompany}
            error={errors.company}
            touched={"company" in errors}
          />
          <TextField
            name="email_address"
            label="Email Address"
            type="email"
            value={attributes.email_address}
            onChange={setAttribute("email_address")}
            errors={errors}
          />
          <TextField
            name="primary_telephone_number"
            label="Phone Number"
            value={attributes.primary_telephone_number}
            onChange={setAttribute("primary_telephone_number")}
            errors={errors}
          />
        </div>
      </div>
      <div className="modal-commands">
        <div className="right-buttons">
          <Button onClick={handleCreate}>Create</Button>
        </div>
      </div>
    </>
  )
}

export default function useCreateContactModal() {
  const { showModal } = useModals()

  return async function ({ name }) {
    return await showModal({
      title: "Add New Contact",
      fullScreen: false,
      heightPixels: 560,
      widthPixels: 600,
      component: ({ respond }) => (
        <CreateContactModal name={name} respond={respond} />
      ),
    })
  }
}
